import React from "react";
import {
  AutoCompleteComponent,
  CheckboxComponent,
  NumberField,
  SelectFieldComponent,
  TextFieldComponent,
} from "../../../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import { RoleKey } from "../../../types/RoleKey";
import { BillingPolicy } from "../../../types/BillingPolicy";
import { DateFieldComponent } from "../../DatePicker";
import { WorkingHours } from "../../../utils/enums/WorkingHours";
import AutoCompleteUIDTO from "../../../dto/AutoCompleteUIDTO";

interface Props {
  control: any;
  register: any;
  errors: any;
  newBillingPolicy: boolean;
  onChangeBillingPolicy: (event: any) => void;
  disabledBonusHours: boolean;
  onChangeBonus: (event: any) => void;
  readonlyShadowCheckbox: boolean;
  onCheckShadow: (event: any) => void;
  userNames?: AutoCompleteUIDTO[];
  isEditing?: boolean;
  defaultBillingPolicy?: string;
}

function PositionFormFields({
  control,
  register,
  errors,
  newBillingPolicy,
  onChangeBillingPolicy,
  disabledBonusHours,
  onChangeBonus,
  readonlyShadowCheckbox,
  onCheckShadow,
  userNames,
  isEditing = true,
  defaultBillingPolicy,
}: Props) {
  return (
    <>
      <NumberField
        defaultValue={WorkingHours.DEFAULT}
        control={control}
        register={register}
        errors={errors}
        propertyName={"internalHours"}
        label="Internal Hours"
        required={true}
      />
      <NumberField
        defaultValue={WorkingHours.DEFAULT}
        control={control}
        register={register}
        errors={errors}
        propertyName={"externalHours"}
        label="External Hours"
        required={true}
      />
      <TextFieldComponent
        control={control}
        register={register}
        errors={errors}
        propertyName={"externalRoleName"}
        label="External Role Name"
        required={true}
      />
      <TextFieldComponent
        control={control}
        register={register}
        errors={errors}
        propertyName={"internalRoleName"}
        label="Internal Role Name"
        required={true}
      />
      <SelectFieldComponent
        control={control}
        register={register}
        errors={errors}
        propertyName={"roleKey"}
        label="Role Key"
        required={true}
        options={[
          {
            value: RoleKey[RoleKey.SOFTWARE_ENGINEER],
            label: RoleKey.SOFTWARE_ENGINEER.toString(),
          },
          {
            value: RoleKey[RoleKey.TECH_LEAD],
            label: RoleKey.TECH_LEAD.toString(),
          },
          {
            value: RoleKey[RoleKey.QA_ENGINEER],
            label: RoleKey.QA_ENGINEER.toString(),
          },
          {
            value: RoleKey[RoleKey.PROJECT_MANAGER],
            label: RoleKey.PROJECT_MANAGER.toString(),
          },
          {
            value: RoleKey[RoleKey.CUSTOMER_SUPPORT],
            label: RoleKey.CUSTOMER_SUPPORT.toString(),
          },
        ]}
      />
      <SelectFieldComponent
        control={control}
        register={register}
        errors={errors}
        propertyName={"billingPolicy"}
        label="Billing Policy"
        required={true}
        defaultValue={defaultBillingPolicy ?? null}
        options={[
          {
            value: BillingPolicy[BillingPolicy.SUM_HOURS],
            label: BillingPolicy.SUM_HOURS.toString(),
          },
          {
            value: BillingPolicy[BillingPolicy.SUM_HOURS_WITH_MINIMUM],
            label: BillingPolicy.SUM_HOURS_WITH_MINIMUM.toString(),
          },
        ]}
        internalSelectProps={{
          onChange: (event) => onChangeBillingPolicy(event),
        }}
      />
      {newBillingPolicy && (
        <NumberField
          control={control}
          register={register}
          errors={errors}
          propertyName={"monthlyMinimumHours"}
          label="Monthly Minimum Hours"
          defaultValue="176"
          required={newBillingPolicy}
          pattern={new RegExp("^[1-9]+[0-9]*$")}
          customHelperText={"Monthly minimum hours should be greater than zero"}
          min={0}
          enableEditInputProps
        />
      )}
      <DateFieldComponent
        control={control}
        register={register}
        errors={errors}
        propertyName={"shortStartDate"}
        label="Start Date"
        required={true}
        clearable={true}
        toUTC
      />
      <DateFieldComponent
        control={control}
        register={register}
        errors={errors}
        propertyName={"shortEndDate"}
        label="End Date"
        required={false}
        clearable={true}
        toUTC
      />
      {!newBillingPolicy && (
        <NumberField
          control={control}
          register={register}
          errors={errors}
          propertyName={"bonus"}
          label="Bonus %"
          required={false}
          defaultValue={"0"}
          pattern={new RegExp("^[0-9]$|^[1-9][0-9]$|^(100)$")}
          customHelperText={"Bonus should be between 0 and 100"}
          readOnly={disabledBonusHours}
          enableEditInputProps
          max={100}
          min={0}
          limitRange
          internalProps={{
            onChange: (event) => onChangeBonus(event),
          }}
        />
      )}
      {userNames && !isEditing && (
        <AutoCompleteComponent
          control={control}
          register={register}
          errors={errors}
          propertyName={"assigneeExternalId"}
          label="Assignee"
          required={false}
          options={userNames}
        />
      )}
      <div style={{ display: "flex", gap: "15px" }}>
        <CheckboxComponent
          control={control}
          register={register}
          errors={errors}
          propertyName={"shadow"}
          label="Shadow Position"
          required={false}
          readOnly={readonlyShadowCheckbox}
          onChange={onCheckShadow}
        />
        <CheckboxComponent
          control={control}
          register={register}
          errors={errors}
          propertyName={"onDemand"}
          label="On Demand"
          required={false}
        />
      </div>
    </>
  );
}

export default PositionFormFields;
